import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { LiveTranscriptBox } from '@/components/voice/LiveTranscriptBox';
import { createVoiceNote } from '@/lib/api/capture';
import { MAX_VOICE_NOTES_PER_CAPTURE, type CaptureRecord } from '@/lib/api/types';
import { NOTE_FAILURE_WORDS, NOTE_PURPOSE_LABEL, formatDuration } from './review-model';

interface VoiceNotesPanelProps {
  record: CaptureRecord;
  /** Read the record again once a new note is registered. */
  onRecorded: () => void;
}

/**
 * The voice notes on the capture with their transcripts (INTERFACES.md
 * 15.3). A note still with Amazon Transcribe says so, and the screen keeps
 * reading the record until it lands. A record from before voice notes
 * shipped carries only `voiceTranscript`, which renders on its own.
 *
 * Recording is live (CAPTURE_JOBS.md section 3): `LiveTranscriptBox`
 * streams to Amazon Transcribe and shows the words as the operator
 * speaks, in place of the earlier record-then-upload clip. Saving posts
 * the edited text as a `transcript` with no `audioKey`.
 */
export function VoiceNotesPanel({ record, onRecorded }: VoiceNotesPanelProps) {
  const notes = record.voiceNotes ?? [];
  const full = notes.length >= MAX_VOICE_NOTES_PER_CAPTURE;

  async function handleSave(transcript: string, durationMs: number) {
    await createVoiceNote(record.captureId, {
      noteId: crypto.randomUUID(),
      transcript,
      durationMs,
      purpose: 'NOTE',
    });
    onRecorded();
  }

  return (
    <section aria-label="Voice notes" className="flex flex-col gap-2 py-3">
      {!full && <LiveTranscriptBox onSave={handleSave} />}

      {notes.length === 0 && record.voiceTranscript && (
        <p className="rounded-card border border-border p-3 text-sm">&ldquo;{record.voiceTranscript}&rdquo;</p>
      )}

      {notes.length === 0 && !record.voiceTranscript && (
        <p className="text-sm text-muted">No notes</p>
      )}

      {notes.length > 0 && (
        <ul className="flex flex-col gap-2">
          {notes.map((note) => {
            const pending = note.status === 'TRANSCRIBING' || note.status === 'UPLOADED';
            return (
              <li key={note.noteId} className="flex flex-col gap-2 rounded-card border border-border p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-muted">
                    {NOTE_PURPOSE_LABEL[note.purpose]}, {formatDuration(note.durationMs)}
                  </span>
                  {note.status === 'TRANSCRIBED' && note.transcriptConfidence !== undefined && (
                    <Badge tone={note.transcriptConfidence < 0.7 ? 'warning' : 'neutral'}>
                      {Math.round(note.transcriptConfidence * 100)}% heard
                    </Badge>
                  )}
                  {note.status === 'FAILED' && <Badge tone="danger">Not transcribed</Badge>}
                </div>
                {pending && (
                  <p className="flex items-center gap-2 text-muted" aria-live="polite">
                    <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden="true" />
                    Transcribing
                  </p>
                )}
                {note.status === 'TRANSCRIBED' && (
                  <p className="break-words">&ldquo;{note.transcript ?? ''}&rdquo;</p>
                )}
                {note.status === 'FAILED' && (
                  <p className="text-muted">{NOTE_FAILURE_WORDS[note.failureReason ?? 'UNKNOWN']}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
